import React, { useState, useEffect, useCallback } from 'react';
import { FaBoxOpen, FaPlus, FaSave, FaTimes, FaHistory, FaTrash } from 'react-icons/fa';
import { productsApi, stockApi } from '../services/api';
import './InventoryAdjustments.css';

const InventoryAdjustments = () => {
    // State for products and adjustments
    const [products, setProducts] = useState([]);
    const [adjustments, setAdjustments] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for current adjustment
    const [isCreatingAdjustment, setIsCreatingAdjustment] = useState(false);
    const [currentAdjustment, setCurrentAdjustment] = useState({
        id: null,
        date: '',
        type: 'write-off',
        reason: '',
        notes: '',
        items: []
    });

    // State for viewing adjustment details
    const [viewingAdjustment, setViewingAdjustment] = useState(null);

    // State for search and filter
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [showHistory, setShowHistory] = useState(false);
    const [historyFilters, setHistoryFilters] = useState({
        startDate: '',
        endDate: '',
        productId: '',
        type: ''
    });

    // Fetch products and adjustments when component mounts
    useEffect(() => {
        fetchProducts();
        fetchAdjustments();
    }, []);

    // Fetch products from API
    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await productsApi.getAll();
            setProducts(Array.isArray(response.data) ? response.data : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to fetch products. Please try again later.');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch adjustments from API
    const fetchAdjustments = async () => {
        try {
            const response = await stockApi.getAll();
            // Filter for only adjustment transactions
            const adjustmentTransactions = Array.isArray(response.data) 
                ? response.data.filter(t => ['write-off', 'damage', 'theft', 'found'].includes(t.action))
                : [];
            setAdjustments(adjustmentTransactions);
        } catch (err) {
            console.error('Error fetching adjustments:', err);
            setAdjustments([]);
        }
    };

    // Filter products based on search term
    const filteredProducts = products.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter adjustments based on type
    const filteredAdjustments = adjustments.filter(adjustment => 
        filterType === 'all' || adjustment.action === filterType
    );

    // Start a new adjustment
    const startNewAdjustment = () => {
        setCurrentAdjustment({
            id: Date.now(),
            date: new Date().toISOString().split('T')[0],
            type: 'write-off',
            reason: '',
            notes: '',
            items: []
        });
        setIsCreatingAdjustment(true);
    };

    // Handle adjustment type change
    const handleTypeChange = (e) => {
        setCurrentAdjustment({
            ...currentAdjustment,
            type: e.target.value
        });
    };

    // Handle adjustment field changes
    const handleAdjustmentChange = (e) => {
        const { name, value } = e.target;
        setCurrentAdjustment({
            ...currentAdjustment,
            [name]: value
        });
    };

    // Add product to adjustment
    const addProductToAdjustment = (productId) => {
        const product = products.find(p => p.id === productId);
        
        if (product) {
            // Check if product is already in the adjustment
            const existingItem = currentAdjustment.items.find(item => item.productId === productId);
            
            if (existingItem) {
                // Update quantity if already exists
                setCurrentAdjustment({
                    ...currentAdjustment,
                    items: currentAdjustment.items.map(item => 
                        item.productId === productId 
                            ? { ...item, quantity: item.quantity + 1 } 
                            : item
                    )
                });
            } else {
                // Add new item
                setCurrentAdjustment({
                    ...currentAdjustment,
                    items: [
                        ...currentAdjustment.items,
                        {
                            productId,
                            productName: product.name,
                            currentStock: product.stock,
                            quantity: 1,
                            notes: ''
                        }
                    ]
                });
            }
        }
    };

    // Update item quantity
    const updateItemQuantity = (productId, quantity) => {
        setCurrentAdjustment({
            ...currentAdjustment,
            items: currentAdjustment.items.map(item => 
                item.productId === productId 
                    ? { ...item, quantity: parseInt(quantity) || 0 } 
                    : item
            )
        });
    };

    // Update item notes
    const updateItemNotes = (productId, notes) => {
        setCurrentAdjustment({
            ...currentAdjustment,
            items: currentAdjustment.items.map(item => 
                item.productId === productId 
                    ? { ...item, notes } 
                    : item
            )
        });
    };

    // Remove item from adjustment
    const removeItem = (productId) => {
        setCurrentAdjustment({
            ...currentAdjustment,
            items: currentAdjustment.items.filter(item => item.productId !== productId)
        });
    };

    // Save the adjustment
    const saveAdjustment = async () => {
        try {
            // Validate adjustment
            if (currentAdjustment.items.length === 0) {
                alert('Please add at least one product to the adjustment');
                return;
            }

            if (!currentAdjustment.reason) {
                alert('Please provide a reason for the adjustment');
                return;
            }

            // Update product stock levels and record transactions
            for (const item of currentAdjustment.items) {
                const product = products.find(p => p.id === item.productId);
                let newStock = product.stock;
                
                // Adjust stock based on adjustment type
                switch (currentAdjustment.type) {
                    case 'write-off':
                    case 'damage':
                    case 'theft':
                        // Decrease stock
                        newStock = Math.max(0, product.stock - item.quantity);
                        break;
                    case 'found':
                        // Increase stock
                        newStock = product.stock + item.quantity;
                        break;
                    default:
                        break;
                }
                
                // Update product stock
                await productsApi.update(product.id, { stock: newStock });
                
                // Record the transaction
                const transactionData = {
                    productId: product.id,
                    type: ['write-off', 'damage', 'theft'].includes(currentAdjustment.type) ? 'out' : 'in',
                    quantity: item.quantity,
                    reason: currentAdjustment.reason,
                    notes: item.notes,
                    transactionDate: new Date().toISOString(),
                    unitPrice: product.price || 0,
                    totalAmount: (product.price || 0) * item.quantity
                };

                console.log('Sending stock transaction to database:', {
                    timestamp: new Date().toISOString(),
                    transactionData: transactionData,
                    productDetails: {
                        id: product.id,
                        name: product.name,
                        currentStock: product.stock
                    },
                    adjustmentDetails: {
                        type: currentAdjustment.type,
                        reason: currentAdjustment.reason
                    }
                });

                await stockApi.create(transactionData);
                
                console.log('Stock transaction successfully added to database');
            }
            
            // Refresh data
            await fetchProducts();
            await fetchAdjustments();
            
            // Reset current adjustment
            setCurrentAdjustment({
                id: null,
                date: '',
                type: 'write-off',
                reason: '',
                notes: '',
                items: []
            });
            
            setIsCreatingAdjustment(false);
        } catch (err) {
            console.error('Error saving adjustment:', err);
            setError('Failed to save adjustment. Please try again.');
        }
    };

    // Cancel the current adjustment
    const cancelAdjustment = () => {
        if (window.confirm('Are you sure you want to cancel this adjustment? All changes will be lost.')) {
            setCurrentAdjustment({
                id: null,
                date: '',
                type: 'write-off',
                reason: '',
                notes: '',
                items: []
            });
            setIsCreatingAdjustment(false);
        }
    };

    // Delete an adjustment from history
    const deleteAdjustment = async (adjustmentId) => {
        if (window.confirm('Are you sure you want to delete this adjustment? This action cannot be undone.')) {
            try {
                await stockApi.delete(adjustmentId);
                await fetchAdjustments();
            } catch (err) {
                console.error('Error deleting adjustment:', err);
                setError('Failed to delete adjustment. Please try again.');
            }
        }
    };

    // View adjustment details
    const viewAdjustmentDetails = (adjustmentId) => {
        const adjustment = adjustments.find(adj => adj.id === adjustmentId);
        if (adjustment) {
            setViewingAdjustment(adjustment);
            setShowHistory(true);
        }
    };

    // Close adjustment details
    const closeAdjustmentDetails = () => {
        setViewingAdjustment(null);
    };

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const response = await stockApi.getHistory(historyFilters);
            setHistory(response.data);
        } catch (err) {
            setError('Failed to fetch history');
            console.error('Error fetching history:', err);
        } finally {
            setLoading(false);
        }
    }, [historyFilters]);

    useEffect(() => {
        if (showHistory) {
            fetchHistory();
        }
    }, [showHistory, historyFilters, fetchHistory]);

    if (loading) {
        return <div className="loading">Loading inventory adjustments...</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Inventory Management</h1>
                <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                    {showHistory ? 'Hide History' : 'Show History'}
                </button>
            </div>

            {showHistory && (
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Transaction History</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <input
                            type="date"
                            value={historyFilters.startDate}
                            onChange={(e) => setHistoryFilters({ ...historyFilters, startDate: e.target.value })}
                            className="border rounded p-2"
                        />
                        <input
                            type="date"
                            value={historyFilters.endDate}
                            onChange={(e) => setHistoryFilters({ ...historyFilters, endDate: e.target.value })}
                            className="border rounded p-2"
                        />
                        <select
                            value={historyFilters.productId}
                            onChange={(e) => setHistoryFilters({ ...historyFilters, productId: e.target.value })}
                            className="border rounded p-2"
                        >
                            <option value="">All Products</option>
                            {products.map(product => (
                                <option key={product.id} value={product.id}>{product.name}</option>
                            ))}
                        </select>
                        <select
                            value={historyFilters.type}
                            onChange={(e) => setHistoryFilters({ ...historyFilters, type: e.target.value })}
                            className="border rounded p-2"
                        >
                            <option value="">All Types</option>
                            <option value="add">Add</option>
                            <option value="remove">Remove</option>
                        </select>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border">Date</th>
                                    <th className="py-2 px-4 border">Product</th>
                                    <th className="py-2 px-4 border">Type</th>
                                    <th className="py-2 px-4 border">Amount</th>
                                    <th className="py-2 px-4 border">Reason</th>
                                    <th className="py-2 px-4 border">User</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((record) => (
                                    <tr key={record.id}>
                                        <td className="py-2 px-4 border">{new Date(record.createdAt).toLocaleDateString()}</td>
                                        <td className="py-2 px-4 border">{record.product.name}</td>
                                        <td className="py-2 px-4 border">{record.type}</td>
                                        <td className="py-2 px-4 border">{record.amount}</td>
                                        <td className="py-2 px-4 border">{record.reason}</td>
                                        <td className="py-2 px-4 border">{record.user.name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="inventory-adjustments-container">
                <h1><FaBoxOpen className="icon-space" /> Inventory Adjustments</h1>
                
                {error && <div className="error-message">{error}</div>}
                
                {!isCreatingAdjustment && !showHistory && (
                    <div className="adjustments-dashboard">
                        <div className="adjustments-header">
                            <button className="btn-new-adjustment" onClick={startNewAdjustment}>
                                <FaPlus /> New Adjustment
                            </button>
                            <button 
                                className="btn-view-history" 
                                onClick={() => setShowHistory(true)}
                                disabled={adjustments.length === 0}
                            >
                                <FaHistory /> View Adjustment History
                            </button>
                        </div>
                        
                        <div className="adjustments-summary">
                            <div className="adjustment-stat">
                                <h3>Total Adjustments</h3>
                                <div className="stat-value">{adjustments.length}</div>
                            </div>
                            <div className="adjustment-stat">
                                <h3>Write-offs</h3>
                                <div className="stat-value">
                                    {adjustments.filter(adj => adj.action === 'write-off').length}
                                </div>
                            </div>
                            <div className="adjustment-stat">
                                <h3>Damages</h3>
                                <div className="stat-value">
                                    {adjustments.filter(adj => adj.action === 'damage').length}
                                </div>
                            </div>
                        </div>
                        
                        {adjustments.length > 0 && (
                            <div className="recent-adjustments">
                                <h2>Recent Adjustments</h2>
                                <div className="adjustments-list">
                                    {adjustments.slice().reverse().slice(0, 5).map(adjustment => (
                                        <div key={adjustment.id} className="adjustment-item">
                                            <div className="adjustment-info">
                                                <div className="adjustment-date">
                                                    {new Date(adjustment.date).toLocaleString()}
                                                </div>
                                                <div className={`adjustment-type type-${adjustment.action}`}>
                                                    {adjustment.action}
                                                </div>
                                            </div>
                                            <div className="adjustment-actions">
                                                <button 
                                                    className="btn-view" 
                                                    onClick={() => viewAdjustmentDetails(adjustment.id)}
                                                >
                                                    View Details
                                                </button>
                                                <button 
                                                    className="btn-delete" 
                                                    onClick={() => deleteAdjustment(adjustment.id)}
                                                >
                                                    <FaTrash /> Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {isCreatingAdjustment && (
                    <div className="adjustment-form">
                        <div className="form-header">
                            <h2>New Inventory Adjustment</h2>
                            <div className="form-actions">
                                <button className="btn-save" onClick={saveAdjustment}>
                                    <FaSave /> Save Adjustment
                                </button>
                                <button className="btn-cancel" onClick={cancelAdjustment}>
                                    <FaTimes /> Cancel
                                </button>
                            </div>
                        </div>
                        
                        <div className="form-body">
                            <div className="form-group">
                                <label htmlFor="adjustmentType">Adjustment Type</label>
                                <select 
                                    id="adjustmentType"
                                    className="form-control"
                                    value={currentAdjustment.type}
                                    onChange={handleTypeChange}
                                >
                                    <option value="write-off">Write-off</option>
                                    <option value="damage">Damage</option>
                                    <option value="theft">Theft</option>
                                    <option value="found">Found</option>
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="adjustmentReason">Reason</label>
                                <input 
                                    id="adjustmentReason"
                                    type="text"
                                    className="form-control"
                                    name="reason"
                                    value={currentAdjustment.reason}
                                    onChange={handleAdjustmentChange}
                                    placeholder="Enter reason for adjustment"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="adjustmentNotes">Notes</label>
                                <textarea 
                                    id="adjustmentNotes"
                                    className="form-control"
                                    name="notes"
                                    value={currentAdjustment.notes}
                                    onChange={handleAdjustmentChange}
                                    placeholder="Enter any additional notes"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Products</label>
                                <div className="product-search">
                                    <input 
                                        type="text"
                                        className="form-control"
                                        placeholder="Search products..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                
                                <div className="product-list">
                                    {filteredProducts.map(product => (
                                        <div key={product.id} className="product-item">
                                            <div className="product-info">
                                                <span className="product-name">{product.name}</span>
                                                <span className="product-stock">Stock: {product.stock}</span>
                                            </div>
                                            <button 
                                                className="btn-add"
                                                onClick={() => addProductToAdjustment(product.id)}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {currentAdjustment.items.length > 0 && (
                                <div className="adjustment-items">
                                    <h3>Selected Products</h3>
                                    <div className="items-list">
                                        {currentAdjustment.items.map(item => (
                                            <div key={item.productId} className="adjustment-item">
                                                <div className="item-info">
                                                    <span className="item-name">{item.productName}</span>
                                                    <div className="item-controls">
                                                        <input 
                                                            type="number"
                                                            min="0"
                                                            value={item.quantity}
                                                            onChange={(e) => updateItemQuantity(item.productId, e.target.value)}
                                                        />
                                                        <input 
                                                            type="text"
                                                            placeholder="Notes"
                                                            value={item.notes}
                                                            onChange={(e) => updateItemNotes(item.productId, e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <button 
                                                    className="btn-remove"
                                                    onClick={() => removeItem(item.productId)}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                
                {showHistory && (
                    <div className="adjustment-history">
                        <div className="history-header">
                            <h2>Adjustment History</h2>
                            <button 
                                className="btn-back"
                                onClick={() => {
                                    setShowHistory(false);
                                    closeAdjustmentDetails();
                                }}
                            >
                                Back to Dashboard
                            </button>
                        </div>
                        
                        {viewingAdjustment && (
                            <div className="adjustment-details">
                                <div className="details-header">
                                    <h3>Adjustment Details</h3>
                                    <button 
                                        className="btn-close"
                                        onClick={closeAdjustmentDetails}
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                                <div className="details-content">
                                    <div className="detail-row">
                                        <span className="detail-label">Date:</span>
                                        <span className="detail-value">
                                            {new Date(viewingAdjustment.date).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Type:</span>
                                        <span className={`detail-value badge badge-${viewingAdjustment.action}`}>
                                            {viewingAdjustment.action}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Product:</span>
                                        <span className="detail-value">{viewingAdjustment.productName}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Quantity:</span>
                                        <span className="detail-value">{viewingAdjustment.amount}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Reason:</span>
                                        <span className="detail-value">{viewingAdjustment.reason}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Notes:</span>
                                        <span className="detail-value">{viewingAdjustment.notes}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="history-filters">
                            <select 
                                className="form-control"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">All Types</option>
                                <option value="write-off">Write-offs</option>
                                <option value="damage">Damages</option>
                                <option value="theft">Thefts</option>
                                <option value="found">Found Items</option>
                            </select>
                        </div>
                        
                        <div className="history-table-container">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>Date & Time</th>
                                        <th>Type</th>
                                        <th>Product</th>
                                        <th>Quantity</th>
                                        <th>Reason</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAdjustments.map(adjustment => (
                                        <tr key={adjustment.id}>
                                            <td>{new Date(adjustment.date).toLocaleString()}</td>
                                            <td>
                                                <span className={`badge badge-${adjustment.action}`}>
                                                    {adjustment.action}
                                                </span>
                                            </td>
                                            <td>{adjustment.productName}</td>
                                            <td>{adjustment.amount}</td>
                                            <td>{adjustment.reason}</td>
                                            <td>{adjustment.notes}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InventoryAdjustments; 